import { useEffect, useRef } from "react";

/* Drag-to-scroll with momentum — extracted from UGCSection so the
   carousel/strip logic can be reused (and unit-tested in isolation).
   Returns a `{ stripRef, onPointerDown, onPointerMove, endDrag, onClickCapture }`
   bag you spread onto the scroll container.
   - Touch / pen pointers are skipped; we let the browser handle native
     swipe momentum which is buttery and respects platform conventions.
   - Mouse drags get a JS inertia engine (exponential decay) so the
     strip glides after release instead of stopping dead.
   - Scroll-snap is temporarily disabled during drag so the strip
     doesn't fight the user's hand; re-enabled once momentum settles.
   - Click capture suppresses click events when the user actually
     dragged > 6 px so cards don't fire onClick on every flick. */
export function useDragCarousel() {
  const stripRef = useRef(null);
  const dragRef = useRef({
    down: false,
    startX: 0,
    lastX: 0,
    lastT: 0,
    velocity: 0, // px/ms
    startScroll: 0,
    moved: 0,
  });
  const momentumRafRef = useRef(null);

  const cancelMomentum = () => {
    if (momentumRafRef.current) {
      cancelAnimationFrame(momentumRafRef.current);
      momentumRafRef.current = null;
    }
  };

  const startMomentum = () => {
    if (!stripRef.current) return;
    let v = dragRef.current.velocity; // px/ms
    if (Math.abs(v) < 0.05) {
      stripRef.current.style.scrollSnapType = "x mandatory";
      return;
    }
    let lastT = performance.now();
    const decay = 0.94; // per ~16ms frame
    const step = () => {
      if (!stripRef.current) return;
      const now = performance.now();
      const dt = now - lastT;
      lastT = now;
      stripRef.current.scrollLeft += v * dt;
      v *= Math.pow(decay, dt / 16);
      if (Math.abs(v) < 0.02) {
        stripRef.current.style.scrollSnapType = "x mandatory";
        momentumRafRef.current = null;
        return;
      }
      momentumRafRef.current = requestAnimationFrame(step);
    };
    momentumRafRef.current = requestAnimationFrame(step);
  };

  const onPointerDown = (e) => {
    if (!stripRef.current) return;
    // Touch / pen — let the browser handle horizontal scroll natively.
    if (e.pointerType === "touch" || e.pointerType === "pen") return;
    // Only intercept primary mouse button — leave wheel & right-click alone.
    if (e.pointerType === "mouse" && e.button !== 0) return;
    cancelMomentum();
    stripRef.current.style.scrollSnapType = "none";
    dragRef.current = {
      down: true,
      startX: e.clientX,
      lastX: e.clientX,
      lastT: performance.now(),
      velocity: 0,
      startScroll: stripRef.current.scrollLeft,
      moved: 0,
      captured: false,
    };
    stripRef.current.style.cursor = "grabbing";
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.down || !stripRef.current) return;
    if (e.pointerType === "touch" || e.pointerType === "pen") return;
    e.preventDefault();
    const now = performance.now();
    const dt = Math.max(1, now - d.lastT);
    const dx = e.clientX - d.lastX;
    d.velocity = 0.6 * d.velocity + 0.4 * (-dx / dt);
    d.lastX = e.clientX;
    d.lastT = now;
    d.moved = Math.abs(e.clientX - d.startX);
    // Capture the pointer only once an actual drag starts (>6px).
    // Capturing on every mousedown retargets the subsequent `click`
    // to the strip, which silently breaks play/mute buttons on cards.
    if (d.moved > 6 && !d.captured) {
      d.captured = true;
      try {
        stripRef.current.setPointerCapture(e.pointerId);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.debug("setPointerCapture unsupported:", err);
        }
      }
    }
    stripRef.current.scrollLeft = d.startScroll - (e.clientX - d.startX);
  };

  const endDrag = (e) => {
    const d = dragRef.current;
    if (!d.down || !stripRef.current) return;
    d.down = false;
    stripRef.current.style.cursor = "grab";
    if (d.captured && e?.pointerId !== undefined) {
      d.captured = false;
      try {
        stripRef.current.releasePointerCapture(e.pointerId);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.debug("releasePointerCapture unsupported:", err);
        }
      }
    }
    startMomentum();
  };

  // Suppress card clicks when the user actually dragged > 6 px.
  const onClickCapture = (e) => {
    if (dragRef.current.moved > 6) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Cancel any in-flight momentum on unmount.
  useEffect(() => () => cancelMomentum(), []);

  return { stripRef, onPointerDown, onPointerMove, endDrag, onClickCapture };
}
